/**
 * **SCRAMBLE** — `PS.EXE`'s tenth mission type, and the only one that starts
 * with the wheels on the ground.
 *
 * The Mission Builder manual gives the recipe outright: *"To Scramble: Issue
 * the Dogfight Order, start the group at zero altitude and keep the group's
 * second path point above the aerodrome but at a higher altitude. The group
 * will rise to the specified height and circle."* That is exactly what this
 * builds — the player's flight parked on its own aerodrome, a raid already
 * inbound and low, and a route whose first leg is a climb over the field.
 *
 * Geometry is `mission-world.ts`'s: the front is `z = 0`, home is `+z`, and
 * heading 0 points at the enemy. The aerodrome sits well inside friendly
 * territory and the raid comes across the wire at it.
 *
 * **Three things here are port-side and stated rather than implied.** The
 * aerodrome's *name* is a real `AERO.DAT` field for the region and side; its
 * world *position* is not, because docs/formats/world-terrain.md §2.4 leaves the
 * map-pixel ↔ world transform unresolved. The runway *heading* is not in any
 * decoded file either — this one takes off toward the front, which is the only
 * direction a scramble has a reason to prefer. And the raid's timing is chosen,
 * not recovered: the enemy is placed far enough out that a pilot who flies the
 * manual's take-off is airborne before they arrive, and close enough that one
 * who dawdles is caught on the climb.
 */

import { aerodromesIn } from "../campaign/aerodromes.ts";
import type { AircraftKey } from "../flight/aircraft-table.ts";
import type { AiSkill } from "../combat/skill.ts";
import { assembleMission } from "./mission-assemble.ts";
import { buildFlight, briefingLines, waypoint } from "./mission-build.ts";
import { missionContext, type MissionContext, type MissionOptions } from "./mission-options.ts";
import { scoutsFor } from "./mission-roster.ts";
import type { MissionBase, MissionFlight, MissionSpec } from "./mission-spec.ts";
import type { MissionObjective } from "./objectives.ts";
import { REGION_LABELS } from "./mission-world.ts";

/**
 * How far inside friendly territory an aerodrome sits. Well clear of the wire
 * — a field on the front line is not an aerodrome. Shared with every other
 * generator's own aerodrome-start option (`homeAerodromeAt`) below, since the
 * distance is a port-side choice either way and one number is one less magic
 * constant to keep in sync.
 */
export const AERODROME_Z_M = 4200;
/** How close to the field counts as being at it, for the landing objective. */
export const AERODROME_RADIUS_M = 600;
/**
 * Height above the field the player must reach for the take-off objective to
 * count — 60 m, a little over the 40 m at which `autopilot.ts`'s `climbingOut`
 * hands an AI back to ordinary flying. High enough that a bounce is not a
 * take-off; low enough that a scramble is not a climb-to-altitude mission.
 */
export const SCRAMBLE_CLEARANCE_M = 60;
/** Where the raid forms up, and how high it comes in. */
const RAID_ALTITUDE_M = 900;
const RAID_APPROACH_Z_M = -1800;

const SKILL_LADDER: readonly AiSkill[] = ["regular", "regular", "veteran", "ace"];

/**
 * A real `AERO.DAT` field for the player's region and side, or the region
 * label if that region has none of theirs, `distanceM` inside friendly
 * territory. Every generator that can put the player's flight on its own
 * aerodrome (Scramble always; the rest when `MissionOptions.aerodromeStart`
 * asks for it, `mission-options.ts`) reads this rather than picking its own
 * field, so a career or a single mission never disagrees with itself about
 * which of the region's aerodromes it started on.
 */
export function homeAerodromeAt(
  context: MissionContext,
  distanceM: number = AERODROME_Z_M,
  radiusM: number = AERODROME_RADIUS_M,
): MissionBase {
  const fields = aerodromesIn(context.region, context.nation);
  const name = fields.length > 0 ? context.draw.pick(fields).name : REGION_LABELS[context.region];
  return {
    name,
    position: { x: 0, y: 0, z: distanceM },
    // Heading 0 is toward the front (`mission-world.ts`): the way a
    // ground-started flight wants to be pointing before it has climbed out.
    headingRad: 0,
    radiusM,
  };
}

/** The player's flight, on the ground, plus a wingman on the grass beside him. */
function scrambleFlights(
  context: MissionContext,
  base: MissionBase,
  paired: boolean,
  ammunition?: "regular" | "incendiary",
): MissionFlight[] {
  const overhead = waypoint({ x: base.position.x, y: RAID_ALTITUDE_M, z: base.position.z }, "CLIMB");
  const intercept = waypoint({ x: 0, y: RAID_ALTITUDE_M, z: base.position.z - 1500 }, "INTERCEPT");
  const flights: MissionFlight[] = [
    buildFlight({
      id: "player",
      side: "friendly",
      aircraft: context.playerAircraft,
      callsign: "You",
      position: base.position,
      headingRad: base.headingRad,
      onGround: true,
      ammunition,
      waypoints: [overhead, intercept],
    }),
  ];
  if (paired) {
    flights.push(
      buildFlight({
        id: "wingman",
        side: "friendly",
        aircraft: context.playerAircraft,
        callsign: "Wingman",
        // Beside him on the field, not behind: two aeroplanes rolling nose to
        // tail off one strip is a collision, not a scramble.
        position: { x: base.position.x + 60, y: base.position.y, z: base.position.z },
        headingRad: base.headingRad,
        onGround: true,
        skill: "regular",
        ammunition,
        waypoints: [overhead, intercept],
      }),
    );
  }
  return flights;
}

function raidFlights(context: MissionContext, base: MissionBase, count: number, enemyAircraft?: AircraftKey): MissionFlight[] {
  const aircraft = enemyAircraft ?? context.draw.pick(scoutsFor(context.enemyNation, context.era));
  const target = waypoint({ x: base.position.x, y: RAID_ALTITUDE_M, z: base.position.z }, "AERODROME");
  return Array.from({ length: count }, (_unused, index) =>
    buildFlight({
      id: `hostile-${index + 1}`,
      side: "hostile",
      aircraft,
      callsign: `Raider ${index + 1}`,
      position: {
        x: index * 130 - (count - 1) * 65,
        y: RAID_ALTITUDE_M + index * 60,
        z: RAID_APPROACH_Z_M - index * 140,
      },
      headingRad: Math.PI,
      skill: context.draw.pick(SKILL_LADDER),
      waypoints: [target],
    }),
  );
}

/**
 * The raid is drawn from the same scout list every other mission uses. It is
 * described to the player as an attack on the aerodrome, and it flies at one:
 * `routeOrders` is left at the default `engage`, so the raiders break off to
 * fight the moment anybody gets airborne near them — which is the whole
 * pressure of the mission.
 */
export function generateScramble(options: MissionOptions): MissionSpec {
  const context = missionContext(options, "scramble");
  const base = homeAerodromeAt(context);
  const paired = context.draw.chance(0.5);
  const raiders = context.draw.int(2, 3);
  const flights = [
    ...scrambleFlights(context, base, paired, options.ammunition),
    ...raidFlights(context, base, raiders, options.enemyAircraft),
  ];
  const enemyAircraft = flights.find((flight) => flight.side === "hostile")?.aircraft ?? context.playerAircraft;

  const objectives: readonly MissionObjective[] = [
    {
      kind: "takeOff",
      id: "get-up",
      label: `Get off the ground at ${base.name}`,
      role: "primary",
      flightId: "player",
      clearanceM: SCRAMBLE_CLEARANCE_M,
    },
    {
      kind: "destroyAircraft",
      id: "break-raid",
      label: raiders === 1 ? "Shoot down the raider" : `Shoot down ${raiders} raiders`,
      role: "primary",
      count: raiders,
    },
    {
      kind: "landAtBase",
      id: "land",
      label: `Land back at ${base.name}`,
      role: "primary",
      flightId: "player",
      position: base.position,
      radiusM: base.radiusM,
    },
  ];

  return assembleMission(context, {
    catalogId: "scramble",
    type: "scramble",
    title: `Scramble: ${base.name}`,
    flights,
    objectives,
    homeBase: base,
    briefing: [
      `The field at ${base.name} is under attack. Get into the air.`,
      ...briefingLines({
        flightSize: paired ? 2 : 1,
        ownAircraft: context.playerAircraft,
        enemyCount: raiders,
        enemySkill: "regular",
        enemyNation: context.enemyNation,
        enemyLeader: "whoever is leading them",
        enemyAircraft,
        region: context.region,
        ownAltitudeM: 0,
        enemyAltitudeM: RAID_ALTITUDE_M,
        weather: context.weather,
      }).slice(1),
    ],
    portNotes: [
      "The aerodrome is a position and a name, not a place: `AERO.DAT` gives every field's name and its pixel on the campaign map, and no decoded file relates that pixel to a world coordinate (docs/formats/world-terrain.md §2.4). The runway heading is ours too — nothing in the data carries one.",
      "There is nothing on the field for the raid to destroy. It comes for the aerodrome, is briefed as coming for the aerodrome, and turns to fight you instead — hangars, tents and parked aeroplanes are not modelled targets.",
    ],
  });
}
